import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Page, Card, Badge, Link, Text, TextField, Spinner, Banner, InlineStack, Box, Pagination, Select, Button, Tooltip } from '@shopify/polaris';
import { getRequest, putRequest } from '../../shared/components/functions.jsx';
import { useLookupStore } from '../../context/useStore.js';
import { TabTitle } from '../../shell/helmet.jsx';
import Toasts from '../../shared/components/toasts/toasts.jsx';

const PAGE_SIZE_OPTIONS = [
  { label: '25 per page', value: '25' },
  { label: '50 per page', value: '50' },
  { label: '100 per page', value: '100' },
  { label: '200 per page', value: '200' }
];

// Helper to format timestamp - handles both milliseconds and other formats
function formatLastCounted(timestamp) {
  if (!timestamp) return null;

  // If it's already a number (milliseconds), use directly
  let date;
  if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'string') {
    // Try parsing as number first (might be string representation of ms)
    const parsed = parseInt(timestamp, 10);
    if (!isNaN(parsed) && parsed > 1000000000000) {
      date = new Date(parsed);
    } else {
      // Try parsing as date string
      date = new Date(timestamp);
    }
  } else {
    date = new Date(timestamp);
  }

  // Check if date is valid
  if (isNaN(date.getTime())) return null;

  const now = new Date();
  const secondsAgo = Math.floor((now - date) / 1000);
  const minutesAgo = Math.floor(secondsAgo / 60);
  const hoursAgo = Math.floor(minutesAgo / 60);

  if (secondsAgo < 60) return 'Just now';
  if (minutesAgo < 2) return '1 minute ago';
  if (minutesAgo < 60) return `${minutesAgo} minutes ago`;
  if (hoursAgo < 2) return '1 hour ago';
  if (date.toDateString() === now.toDateString()) return `${hoursAgo} hour(s) ago`;

  // Yesterday
  let yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${date.toLocaleTimeString()}`;
  }

  // For days older than yesterday
  let options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
}

// Check if timestamp is within the last 2 months
function isCountedRecently(timestamp) {
  if (!timestamp) return false;

  let date;
  if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'string') {
    const parsed = parseInt(timestamp, 10);
    if (!isNaN(parsed) && parsed > 1000000000000) {
      date = new Date(parsed);
    } else {
      date = new Date(timestamp);
    }
  } else {
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) return false;

  const now = new Date();
  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(now.getMonth() - 2);

  return date >= twoMonthsAgo;
}

// Get absolute date string for tooltip
function getAbsoluteDateString(timestamp) {
  if (!timestamp) return null;

  let date;
  if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'string') {
    const parsed = parseInt(timestamp, 10);
    if (!isNaN(parsed) && parsed > 1000000000000) {
      date = new Date(parsed);
    } else {
      date = new Date(timestamp);
    }
  } else {
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) return null;

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function InventoryPage() {
  const { stockplaatsId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pickingplaces } = useLookupStore();
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState({}); // Track original values by counter
  const [loading, setLoading] = useState(true);
  const [savingRows, setSavingRows] = useState({});
  const [toast, setToast] = useState(false);
  const [errorToast, setErrorToast] = useState(false);

  // Initialize state from URL params
  const [page, setPage] = useState(() => parseInt(searchParams.get('page')) || 1);
  const [limit, setLimit] = useState(() => parseInt(searchParams.get('limit')) || 50);
  const [rackFilter, setRackFilter] = useState(() => searchParams.get('rack') || '');
  const [shelfFilter, setShelfFilter] = useState(() => searchParams.get('shelf') || '');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [availableRacks, setAvailableRacks] = useState([]);
  const [availableShelves, setAvailableShelves] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, hasNext: false, hasPrev: false });
  const debounceTimers = useRef({});
  const searchDebounceTimer = useRef(null);
  const prevStockplaatsId = useRef(stockplaatsId);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', String(page));
    if (limit !== 50) params.set('limit', String(limit));
    if (rackFilter) params.set('rack', rackFilter);
    if (shelfFilter) params.set('shelf', shelfFilter);
    if (searchQuery) params.set('search', searchQuery);
    setSearchParams(params, { replace: true });
  }, [page, limit, rackFilter, shelfFilter, searchQuery, setSearchParams]);

  // Reset filters only when stockplaatsId actually changes (not on initial mount)
  useEffect(() => {
    if (prevStockplaatsId.current !== stockplaatsId) {
      setPage(1);
      setRackFilter('');
      setShelfFilter('');
      setSearchQuery('');
      setSearchInput('');
      prevStockplaatsId.current = stockplaatsId;
    }
  }, [stockplaatsId]);

  const stockplaats = pickingplaces.find((p) => p.counter === parseInt(stockplaatsId));
  const pageTitle = stockplaats ? `Inventory: ${stockplaats.pickingplace}` : 'Inventory';

  const fetchInventory = useCallback(async () => {
    if (!stockplaatsId) return;
    setLoading(true);
    try {
      const rackParam = rackFilter !== '' ? `&rack=${rackFilter}` : '';
      const shelfParam = shelfFilter !== '' ? `&shelf=${shelfFilter}` : '';
      const searchParam = searchQuery !== '' ? `&search=${encodeURIComponent(searchQuery)}` : '';
      const data = await getRequest(`/api/inventory/stockplaats/${stockplaatsId}?page=${page}&limit=${limit}${rackParam}${shelfParam}${searchParam}`);
      if (data && Array.isArray(data.items)) {
        setItems(data.items);
        // Store original values for change detection
        const originals = {};
        data.items.forEach((item) => {
          originals[item.counter] = {
            aantal: item.aantal,
            Rack: item.Rack,
            Legplank: item.Legplank
          };
        });
        setOriginalItems(originals);
        setPagination(data.pagination || { total: 0, totalPages: 1, hasNext: false, hasPrev: false });
        if (Array.isArray(data.racks)) {
          setAvailableRacks(data.racks);
        }
        if (Array.isArray(data.shelves)) {
          setAvailableShelves(data.shelves);
        }
      }
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setErrorToast(true);
    } finally {
      setLoading(false);
    }
  }, [stockplaatsId, page, limit, rackFilter, shelfFilter, searchQuery]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handleFieldChange = (counter, field, value) => {
    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.counter === counter) {
          return { ...item, [field]: value };
        }
        return item;
      })
    );

    // Only trigger debounced save for quantity changes
    if (field === 'aantal') {
      if (debounceTimers.current[counter]) {
        clearTimeout(debounceTimers.current[counter]);
      }
      debounceTimers.current[counter] = setTimeout(() => {
        saveRow(counter, true); // true = check for quantity change
      }, 800);
    }
  };

  const saveRow = async (counter, isQuantityUpdate = false) => {
    const item = items.find((i) => i.counter === counter);
    if (!item) return;

    const original = originalItems[counter];
    const currentAantal = parseFloat(item.aantal) || 0;
    const originalAantal = parseFloat(original?.aantal) || 0;
    const currentRack = parseInt(item.Rack) || 0;
    const originalRack = parseInt(original?.Rack) || 0;
    const currentLegplank = parseInt(item.Legplank) || 0;
    const originalLegplank = parseInt(original?.Legplank) || 0;

    // Check if anything actually changed
    const quantityChanged = currentAantal !== originalAantal;
    const rackChanged = currentRack !== originalRack;
    const shelfChanged = currentLegplank !== originalLegplank;

    // If nothing changed, don't save
    if (!quantityChanged && !rackChanged && !shelfChanged) {
      return;
    }

    setSavingRows((prev) => ({ ...prev, [counter]: true }));

    try {
      const payload = {
        Productid: item.Productid,
        Stockplaats: item.Stockplaats,
        aantal: currentAantal,
        Rack: currentRack,
        Legplank: currentLegplank,
        // Only mark as inventory count if quantity changed
        isInventoryCount: quantityChanged
      };

      const result = await putRequest(`/api/inventory/${counter}`, payload);

      if (result) {
        // Update original values
        setOriginalItems((prev) => ({
          ...prev,
          [counter]: { aantal: currentAantal, Rack: currentRack, Legplank: currentLegplank }
        }));

        // Only update lastInventoryCount if quantity was actually changed
        if (quantityChanged) {
          const now = Date.now();
          setItems((prevItems) =>
            prevItems.map((i) => {
              if (i.counter === counter) {
                return { ...i, lastInventoryCount: now };
              }
              return i;
            })
          );
        }
        setToast(true);
      }
    } catch (err) {
      console.error('Error saving inventory:', err);
      setErrorToast(true);
    } finally {
      setSavingRows((prev) => ({ ...prev, [counter]: false }));
    }
  };

  // Mark as counted without changing quantity
  const markAsCounted = async (counter) => {
    const item = items.find((i) => i.counter === counter);
    if (!item) return;

    setSavingRows((prev) => ({ ...prev, [counter]: true }));

    try {
      const payload = {
        Productid: item.Productid,
        Stockplaats: item.Stockplaats,
        aantal: parseFloat(item.aantal) || 0,
        Rack: parseInt(item.Rack) || 0,
        Legplank: parseInt(item.Legplank) || 0,
        isInventoryCount: true // Force inventory count event
      };

      const result = await putRequest(`/api/inventory/${counter}`, payload);

      if (result) {
        const now = Date.now();
        setItems((prevItems) =>
          prevItems.map((i) => {
            if (i.counter === counter) {
              return { ...i, lastInventoryCount: now };
            }
            return i;
          })
        );
        setToast(true);
      }
    } catch (err) {
      console.error('Error marking as counted:', err);
      setErrorToast(true);
    } finally {
      setSavingRows((prev) => ({ ...prev, [counter]: false }));
    }
  };

  const handleBlur = (counter, field) => {
    if (debounceTimers.current[counter]) {
      clearTimeout(debounceTimers.current[counter]);
      delete debounceTimers.current[counter];
    }

    // For rack/shelf changes, save without marking as counted
    if (field === 'Rack' || field === 'Legplank') {
      saveRow(counter, false);
    } else if (field === 'aantal') {
      saveRow(counter, true);
    }
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && pagination.hasNext) {
      setPage((p) => p + 1);
    } else if (direction === 'prev' && pagination.hasPrev) {
      setPage((p) => p - 1);
    }
  };

  const handleLimitChange = (value) => {
    setLimit(parseInt(value));
    setPage(1);
  };

  const handleRackFilterChange = (value) => {
    setRackFilter(value);
    setPage(1);
  };

  const handleShelfFilterChange = (value) => {
    setShelfFilter(value);
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    // Debounce the actual search
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    searchDebounceTimer.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
    }, 400);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(1);
  };

  // Build rack filter options
  const rackFilterOptions = [{ label: 'Rack ≥ 1 (default)', value: '' }, { label: 'All racks', value: 'all' }, ...availableRacks.map((rack) => ({ label: `Rack ${rack}`, value: String(rack) }))];

  // Build shelf filter options
  const shelfFilterOptions = [{ label: 'All shelves', value: '' }, ...availableShelves.map((shelf) => ({ label: `Shelf ${shelf}`, value: String(shelf) }))];

  if (!stockplaatsId) {
    return (
      <Page title="Inventory">
        <Banner tone="warning">Please select a stockplaats from the sidebar menu.</Banner>
      </Page>
    );
  }

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, pagination.total);

  return (
    <Page title={pageTitle} fullWidth>
      <TabTitle title={pageTitle} />
      <Toasts toast={toast} setToast={setToast} errorToast={errorToast} setErrorToast={setErrorToast} toastContent="Inventory updated" />

      <Card>
        {/* Pagination header */}
        <Box padding="400" paddingBlockEnd="200">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="400" blockAlign="center">
              <Text variant="bodyMd" fontWeight="semibold">
                {loading ? 'Loading...' : `${pagination.total} products`}
              </Text>
              {!loading && pagination.total > 0 && (
                <Text variant="bodySm" tone="subdued">
                  Showing {startItem}-{endItem}
                </Text>
              )}
            </InlineStack>
            <InlineStack gap="400" blockAlign="center">
              <div style={{ width: '200px' }}>
                <TextField
                  label="Search"
                  labelHidden
                  placeholder="Search by ID or name..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  clearButton
                  onClearButtonClick={handleSearchClear}
                  autoComplete="off"
                  disabled={loading}
                />
              </div>
              <div style={{ width: '160px' }}>
                <Select label="Filter by rack" labelHidden options={rackFilterOptions} value={rackFilter} onChange={handleRackFilterChange} disabled={loading} />
              </div>
              <div style={{ width: '140px' }}>
                <Select label="Filter by shelf" labelHidden options={shelfFilterOptions} value={shelfFilter} onChange={handleShelfFilterChange} disabled={loading} />
              </div>
              <div style={{ width: '140px' }}>
                <Select options={PAGE_SIZE_OPTIONS} value={String(limit)} onChange={handleLimitChange} disabled={loading} />
              </div>
              <Pagination hasPrevious={pagination.hasPrev} hasNext={pagination.hasNext} onPrevious={() => handlePageChange('prev')} onNext={() => handlePageChange('next')} />
            </InlineStack>
          </InlineStack>
        </Box>

        {loading ? (
          <Box padding="400">
            <InlineStack align="center" gap="200">
              <Spinner size="small" />
              <Text>Loading inventory...</Text>
            </InlineStack>
          </Box>
        ) : items.length === 0 ? (
          <Box padding="400">
            <Banner tone="info">No products found for this stockplaats.</Banner>
          </Box>
        ) : (
          <>
            <style>{`
              .inventory-table tbody tr:hover {
                background-color: #f4f4f5 !important;
              }
              .inventory-table tbody tr.discontinued:hover {
                background-color: #fecaca !important;
              }
            `}</style>
            <div style={{ overflowX: 'auto' }}>
              <table className="inventory-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e1e3e5', backgroundColor: '#f6f6f7' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Product ID</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Reference</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Product Name</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Supplier</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, width: '90px' }}>Purchase</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, width: '90px' }}>Sales</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, width: '140px' }}>Rack</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, width: '100px' }}>Shelf</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, width: '120px' }}>Quantity</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Last Counted</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, width: '130px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const lastCountedText = formatLastCounted(item.lastInventoryCount);
                    const absoluteDate = getAbsoluteDateString(item.lastInventoryCount);
                    const isCounted = isCountedRecently(item.lastInventoryCount);

                    return (
                      <tr
                        key={item.counter}
                        className={item.Discontinued ? 'discontinued' : ''}
                        style={{
                          borderBottom: '1px solid #e1e3e5',
                          backgroundColor: savingRows[item.counter] ? '#f0f8ff' : item.Discontinued ? '#fee2e2' : 'transparent'
                        }}
                      >
                        <td style={{ padding: '4px 12px' }}>
                          <Link removeUnderline url={`/products/${item.Productid}`}>
                            #{item.Productid}
                          </Link>
                        </td>
                        <td style={{ padding: '4px 12px' }}>
                          <Text as="span" tone="subdued">
                            {item.Productref || '-'}
                          </Text>
                        </td>
                        <td style={{ padding: '4px 12px' }}>
                          <Text as="span" truncate>
                            {item.ProductNameNL || '-'}
                          </Text>
                        </td>
                        <td style={{ padding: '4px 12px' }}>
                          {item.SupplierID ? (
                            <Link removeUnderline url={`/companies/${item.SupplierID}`}>
                              {item.SupplierName || `#${item.SupplierID}`}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td style={{ padding: '4px 12px', textAlign: 'right', width: '90px' }}>
                          <Text as="span" tone="subdued" variant="bodySm">
                            {item.PurchasePrice != null ? `€${item.PurchasePrice.toFixed(2)}` : '-'}
                          </Text>
                        </td>
                        <td style={{ padding: '4px 12px', textAlign: 'right', width: '90px' }}>
                          <Text as="span" tone="subdued" variant="bodySm">
                            {item.SalesPrice != null ? `€${item.SalesPrice.toFixed(2)}` : '-'}
                          </Text>
                        </td>
                        <td style={{ padding: '4px 12px', width: '140px' }}>
                          <TextField
                            type="number"
                            value={item.Rack != null ? String(item.Rack) : ''}
                            onChange={(value) => handleFieldChange(item.counter, 'Rack', value)}
                            onBlur={() => handleBlur(item.counter, 'Rack')}
                            autoComplete="off"
                            min={0}
                          />
                        </td>
                        <td style={{ padding: '4px 12px', width: '100px' }}>
                          <TextField
                            type="number"
                            value={item.Legplank != null ? String(item.Legplank) : ''}
                            onChange={(value) => handleFieldChange(item.counter, 'Legplank', value)}
                            onBlur={() => handleBlur(item.counter, 'Legplank')}
                            autoComplete="off"
                            min={0}
                          />
                        </td>
                        <td style={{ padding: '4px 12px', width: '120px' }}>
                          <TextField
                            type="number"
                            value={item.aantal != null ? String(item.aantal) : ''}
                            onChange={(value) => handleFieldChange(item.counter, 'aantal', value)}
                            onBlur={() => handleBlur(item.counter, 'aantal')}
                            autoComplete="off"
                            step="0.01"
                          />
                        </td>
                        <td style={{ padding: '4px 12px' }}>
                          <Text as="span" tone="subdued" variant="bodySm">
                            {lastCountedText || 'Never'}
                          </Text>
                        </td>
                        <td style={{ padding: '4px 12px', textAlign: 'center' }}>
                          {savingRows[item.counter] ? (
                            <Spinner size="small" />
                          ) : isCounted ? (
                            <Tooltip content={`Counted on ${absoluteDate} (within last 2 months)`}>
                              <Badge tone="success">Counted</Badge>
                            </Tooltip>
                          ) : (
                            <Tooltip content={absoluteDate ? `Last counted on ${absoluteDate} (more than 2 months ago)` : 'Never counted at this location'}>
                              <Button size="micro" variant="plain" onClick={() => markAsCounted(item.counter)}>
                                Mark counted
                              </Button>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <Box padding="400" paddingBlockStart="200">
              <InlineStack align="end" blockAlign="center" gap="400">
                <Text variant="bodySm" tone="subdued">
                  Page {page} of {pagination.totalPages}
                </Text>
                <Pagination hasPrevious={pagination.hasPrev} hasNext={pagination.hasNext} onPrevious={() => handlePageChange('prev')} onNext={() => handlePageChange('next')} />
              </InlineStack>
            </Box>
          </>
        )}
      </Card>
    </Page>
  );
}
